import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Menu, X, FastForward, Download, Rewind, Copy, Check, FileText } from 'lucide-react'
import type { SessionMetadata, Participant, Message, Artifact, Reflection, Stage } from '../../types'
import { STAGE_NAMES, STAGE_AGENTS, AGENT_COLORS, AGENT_DISPLAY_NAMES } from '../../types'
import {
  subscribeToSession,
  subscribeToParticipants,
  subscribeToMessages,
  subscribeToArtifacts,
  subscribeToReflections,
  subscribeToFeedback,
  submitReflection,
  forceCompleteReflection,
  getParticipantByUid,
  markIntroSent,
} from '../../lib/session'
import { useAuth } from '../auth/AuthProvider'
import { AIOrchestrator } from '../../lib/ai'
import { downloadSessionBundle } from '../../lib/download'
import StageProgressBar from './StageProgressBar'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import TypingIndicator from './TypingIndicator'
import ReflectionPanel from './ReflectionPanel'
import FeedbackForm from './FeedbackForm'
import SoloCompletionScreen from '../solo/SoloCompletionScreen'

export default function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  const [session, setSession] = useState<SessionMetadata | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [reflections, setReflections] = useState<Reflection[]>([])
  const [menuOpen, setMenuOpen] = useState(false)
  const [connectionError, setConnectionError] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [aiThinking, setAiThinking] = useState(false)
  const [copied, setCopied] = useState(false)
  const [reflectionMinimized, setReflectionMinimized] = useState(false)

  const participantId = sessionStorage.getItem('participantId') || ''
  const displayName = sessionStorage.getItem('displayName') || 'Anonymous'
  // Match by participantId first: it is what the tab that started the session holds,
  // and creatorUid then covers a host who comes back from My Sessions in a new tab.
  const isCreator =
    (!!participantId && session?.createdBy === participantId) ||
    (!!user?.uid && session?.creatorUid === user.uid)

  const orchestratorRef = useRef<AIOrchestrator | null>(null)
  const hasTriggeredInitial = useRef(false)
  const synthesisInFlight = useRef(new Set<string>())
  const sessionUnsubRef = useRef<(() => void) | null>(null)

  // Recover participantId from session data when sessionStorage is empty
  // (e.g. user returns to a session from My Sessions page in a new tab)
  useEffect(() => {
    if (participantId || !session || authLoading) return
    if (!user?.uid) {
      // Anonymous visitor with no identity for this session: send them to rejoin
      if (session.roomCode && !session.isSolo) {
        navigate(`/?join=${session.roomCode}`)
      } else {
        navigate('/')
      }
      return
    }
    if (session.creatorUid === user.uid && session.createdBy) {
      // Creator returning, so we know their participantId from session.createdBy
      sessionStorage.setItem('participantId', session.createdBy)
      // Also try to get display name from participants
      const p = participants.find(p => p.id === session.createdBy)
      if (p) sessionStorage.setItem('displayName', p.displayName)
      window.location.reload()
      return
    }
    // Non-creator: try participant uid lookup (works for sessions created after this fix)
    getParticipantByUid(sessionId!, user.uid).then((result) => {
      if (result) {
        sessionStorage.setItem('participantId', result.participantId)
        sessionStorage.setItem('displayName', result.displayName)
        window.location.reload()
      } else if (session.roomCode) {
        // Lost identity (new tab, or cleared storage): send them to rejoin, code prefilled
        navigate(`/?join=${session.roomCode}`)
      } else {
        navigate('/')
      }
    })
  }, [session, participants, user?.uid, participantId, sessionId, navigate, authLoading])

  // Redirect if no session info and no auth to recover from
  useEffect(() => {
    if (!sessionId) {
      navigate('/')
    }
  }, [sessionId, navigate])

  // Initialize AI orchestrator (all users, for revert + reflection confirmation; lull detection is host-only)
  useEffect(() => {
    if (!sessionId) return
    const orchestrator = new AIOrchestrator(sessionId)
    orchestratorRef.current = orchestrator
    return () => {
      orchestrator.destroy()
      orchestratorRef.current = null
    }
  }, [sessionId])

  // Subscribe to all real-time data
  useEffect(() => {
    if (!sessionId) return

    const sessionUnsub = subscribeToSession(
      sessionId,
      (data) => {
        setSession(data)
        setAiThinking(!!(data as any).aiThinking)
        setConnectionError(false)
      },
      () => setConnectionError(true),
    )
    sessionUnsubRef.current = sessionUnsub

    const unsubs = [
      sessionUnsub,
      subscribeToParticipants(sessionId, setParticipants),
      subscribeToMessages(sessionId, setMessages),
      subscribeToArtifacts(sessionId, setArtifacts),
      subscribeToReflections(sessionId, setReflections),
    ]

    return () => unsubs.forEach((u) => u())
  }, [sessionId])

  // Auto-retry session subscription when connection is lost
  useEffect(() => {
    if (!connectionError || !sessionId) return

    const timer = setTimeout(() => {
      if (sessionUnsubRef.current) {
        sessionUnsubRef.current()
      }
      const newUnsub = subscribeToSession(
        sessionId,
        (data) => {
          setSession(data)
          setAiThinking(!!(data as any).aiThinking)
          setConnectionError(false)
        },
        () => setConnectionError(true),
      )
      sessionUnsubRef.current = newUnsub
    }, 5000)

    return () => clearTimeout(timer)
  }, [connectionError, sessionId])

  // Pass returning/solo session info to orchestrator
  useEffect(() => {
    if (!session || !orchestratorRef.current) return
    if (session.isReturning) {
      orchestratorRef.current.setIsReturning(true)
    }
    if (session.isSolo) {
      orchestratorRef.current.setIsSolo(true)
    }
  }, [session?.isReturning, session?.returningSummary, session?.isSolo])

  // Subscribe to feedback when at Stage 5
  useEffect(() => {
    if (!sessionId || !session || session.currentStage !== 5) return
    return subscribeToFeedback(sessionId, participantId, setFeedbackSubmitted)
  }, [sessionId, participantId, session?.currentStage])

  // Trigger initial AI greeting when session first loads with participants (host only)
  useEffect(() => {
    if (!isCreator) return
    if (!session || !orchestratorRef.current || hasTriggeredInitial.current) return
    if (session.status !== 'active') return
    if (participants.length === 0) return
    if ((session as any).introSent) return  // Firestore-level guard against cross-mount duplicates
    // Returning sessions: wait for the returning summary so Scout doesn't open with the newbie script
    if (session.isReturning && !session.returningSummary) return

    // Only trigger if no agent messages exist yet
    const hasAgentMessage = messages.some(m => m.participantId === 'agent')
    if (!hasAgentMessage) {
      hasTriggeredInitial.current = true
      markIntroSent(sessionId!)  // persist before triggering
      orchestratorRef.current.triggerImmediate(messages, participants, session.currentStage, session.currentAgent, artifacts)
    }
  }, [isCreator, session, participants, messages, artifacts])

  // Sync reflection-active state to orchestrator so lull timer doesn't fire during reflections
  useEffect(() => {
    if (!orchestratorRef.current) return
    const hasActiveReflection = reflections.some(r => !r.allSubmitted)
    orchestratorRef.current.setReflectionActive(hasActiveReflection)
  }, [reflections])

  // Feed activity to AI orchestrator for lull detection (host only)
  useEffect(() => {
    if (!isCreator) return
    if (!session || !orchestratorRef.current || session.status !== 'active') return
    orchestratorRef.current.onActivity(messages, participants, session.currentStage, session.currentAgent, artifacts)
  }, [isCreator, messages, participants, session, artifacts])

  // Synthesize reflections when all submitted (host only)
  useEffect(() => {
    if (!isCreator) return
    if (!session || !orchestratorRef.current) return
    for (const r of reflections) {
      if (r.allSubmitted && !r.synthesisMessageId && !synthesisInFlight.current.has(r.id)) {
        synthesisInFlight.current.add(r.id)
        const responseContents: Record<string, string> = {}
        for (const [pid, resp] of Object.entries(r.responses)) {
          responseContents[pid] = resp.content
        }
        if (Object.keys(responseContents).length === 0) continue  // No responses, nothing to synthesize
        orchestratorRef.current.synthesizeReflections(session.currentAgent, r.id, r.prompt, responseContents, participants)
      }
    }
  }, [reflections, session, participants])


  // When a new reflection appears, start minimized then auto-open after 4s
  const activeReflectionId = reflections.find(r => !r.allSubmitted)?.id
  const prevReflectionId = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!activeReflectionId) {
      prevReflectionId.current = undefined
      return
    }
    // Only trigger staged reveal for a *new* reflection
    if (activeReflectionId === prevReflectionId.current) return
    prevReflectionId.current = activeReflectionId

    // Start minimized so the chat message stays visible
    setReflectionMinimized(true)

    // Auto-open the modal after 4 more seconds
    const timer = setTimeout(() => setReflectionMinimized(false), 4000)
    return () => clearTimeout(timer)
  }, [activeReflectionId])

  // Handle session bundle download
  const handleDownloadSummary = useCallback(async () => {
    if (!session) return
    await downloadSessionBundle(session.sessionName, messages, artifacts, participants, reflections)
  }, [session, messages, artifacts, participants, reflections])

  // Handle stage advancement
  const handleAdvanceStage = useCallback(async () => {
    if (!session || !orchestratorRef.current) return
    const nextStage = (session.currentStage + 1) as Stage
    if (nextStage > 5) return
    const nextAgent = STAGE_AGENTS[nextStage]
    await orchestratorRef.current.performHandoff(
      session.currentAgent,
      nextAgent,
      nextStage,
      messages,
      participants,
      artifacts,
    )
  }, [session, messages, participants, artifacts])

  // Handle stage rollback
  const handleRevertStage = useCallback(async (targetStage: Stage) => {
    if (!session || !orchestratorRef.current) return
    await orchestratorRef.current.performRevert(
      targetStage,
      messages,
      participants,
      artifacts,
    )
  }, [session, messages, participants, artifacts])

  if (!session || !sessionId) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <p className="mono-label text-navy/70">
          Loading session
          <span className="caret" aria-hidden="true" />
        </p>
      </div>
    )
  }

  const currentStage = session.currentStage
  const currentAgent = session.currentAgent || STAGE_AGENTS[currentStage]

  // The server clears aiThinking as soon as it opens the streaming message doc, so keep
  // the thinking line up until that doc actually has text to read. Otherwise the chat
  // would go blank for the moment between the two.
  const showThinking = aiThinking || messages.some(m => m.streaming && !m.content?.trim())

  // Find active reflection for current stage
  const activeReflection = reflections.find(
    (r) => r.stage === currentStage && !r.allSubmitted,
  )

  function handleReflectionSubmit(content: string) {
    if (!activeReflection || !sessionId) return
    submitReflection(sessionId, activeReflection.id, participantId, content, participants.length)
  }

  return (
    <div className="h-dvh flex flex-col bg-stone">
      {/* Fixed Header */}
      <header className="bg-navy text-stone px-4 py-3 flex items-center justify-between shrink-0 border-b-2 border-golden">
        <h1 className="hl text-lg text-stone">Day One</h1>
        <div className="hidden sm:flex items-baseline gap-2">
          <span className="mono-label text-golden">
            Stage {currentStage} of 5
          </span>
          <span className="font-display text-sm text-stone">
            {STAGE_NAMES[currentStage]}
          </span>
        </div>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-1.5 hover:bg-white/10 transition-colors cursor-pointer"
          aria-label={menuOpen ? 'Close session menu' : 'Open session menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {/* Mobile stage label */}
      <div className="sm:hidden bg-navy text-stone text-center pb-2">
        <span className="mono-label text-golden">Stage {currentStage} of 5</span>{' '}
        <span className="font-display text-sm">{STAGE_NAMES[currentStage]}</span>
      </div>

      {/* Slide-out menu */}
      {menuOpen && (
        <div className="absolute top-16 right-3 z-40 card card-shadow py-2 w-60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-2 border-b border-navy/20">
            <p className="mono-label text-teal">Session</p>
            <p className="text-sm font-semibold text-navy truncate">{session.sessionName}</p>
          </div>
          {!session.isSolo && (
          <div className="px-4 py-2 border-b border-navy/20">
            <p className="mono-label text-teal mb-1">Invite others</p>
            <div className="flex items-center gap-2">
              <span className="font-mono text-base font-semibold tracking-[0.3em] text-navy">{session.roomCode}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(session.roomCode)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }}
                className="p-1 hover:bg-paper transition text-navy/70 hover:text-navy cursor-pointer"
                aria-label="Copy room code"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <p className="text-[11px] text-navy/70 mt-1">Share this code so others can join</p>
          </div>
          )}
          <div className="px-4 py-2 border-b border-navy/20">
            <p className="mono-label text-teal">Participants ({participants.length})</p>
            <div className="mt-1 space-y-1">
              {participants.map((p) => (
                <div key={p.id} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] text-white font-semibold border border-navy"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.displayName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs text-navy/80">
                    {p.displayName}
                    {p.id === participantId && (
                      <span className="text-navy/70 ml-1">(you)</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="px-4 py-2 border-b border-navy/20">
            <p className="mono-label text-teal">Artifacts ({artifacts.length})</p>
            {artifacts.length === 0 && (
              <p className="text-xs text-navy/70 mt-1">None yet</p>
            )}
            {artifacts.map((a) => (
              <button
                key={a.id}
                onClick={() => {
                  let blob: Blob
                  if (a.content.startsWith('image:')) {
                    const [mediaType, base64] = a.content.slice(6).split(/:(.*)/s)
                    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
                    blob = new Blob([bytes], { type: mediaType })
                  } else {
                    blob = new Blob([a.content], { type: 'text/plain' })
                  }
                  const url = URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = url
                  link.download = a.fileName
                  link.click()
                  URL.revokeObjectURL(url)
                }}
                className="w-full flex items-center gap-2 mt-1 px-2 py-1.5 text-xs text-navy/75 hover:bg-paper transition-colors cursor-pointer text-left"
              >
                <FileText className="w-3.5 h-3.5 text-navy/70 shrink-0" />
                <span className="truncate flex-1">{a.fileName}</span>
                <Download className="w-3 h-3 text-navy/70 shrink-0" />
              </button>
            ))}
            {artifacts.length > 0 && (
              <button
                onClick={() => { handleDownloadSummary(); setMenuOpen(false) }}
                className="w-full flex items-center justify-center gap-2 mt-2 px-3 py-2 mono-label text-stone bg-navy border-2 border-navy hover:bg-navy-lifted transition-colors cursor-pointer"
              >
                <Download className="w-3.5 h-3.5" />
                Download all
              </button>
            )}
          </div>
          {isCreator && currentStage > 1 && session.status !== 'completed' && (
            <div className="px-4 py-2 border-b border-navy/20">
              <p className="mono-label text-teal mb-1">Go back to</p>
              <div className="space-y-1">
                {([1, 2, 3, 4, 5] as Stage[]).filter(s => s < currentStage).map((s) => (
                  <button
                    key={s}
                    onClick={() => { handleRevertStage(s); setMenuOpen(false) }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-navy/75 hover:bg-paper transition-colors cursor-pointer"
                  >
                    <Rewind className="w-3 h-3" />
                    Stage {s}: {STAGE_NAMES[s]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {isCreator && currentStage < 5 && session.status !== 'completed' && (
            <div className="px-4 py-2">
              <button
                onClick={() => { handleAdvanceStage(); setMenuOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2 mono-label text-white bg-amber border-2 border-navy hover:bg-amber-dark transition-colors cursor-pointer"
              >
                <FastForward className="w-3.5 h-3.5" />
                Advance to Stage {currentStage + 1}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Connection error banner */}
      {connectionError && (
        <div className="bg-brick text-white mono-label text-center py-2 px-4 shrink-0" role="status">
          Connection lost, reconnecting
          <span className="caret" aria-hidden="true" />
        </div>
      )}

      {/* Stage Progress Bar */}
      <StageProgressBar currentStage={currentStage} currentAgent={currentAgent} />

      {/* Scrollable Messages */}
      <MessageList
        messages={messages}
        participants={participants}
        artifacts={artifacts}
        currentParticipantId={participantId}
      />

      {/* Typing Indicator */}
      <TypingIndicator participants={participants} currentParticipantId={participantId} />

      {/* AI Thinking Indicator */}
      {showThinking && (() => {
        // Check if there are recent artifact uploads since the last agent message
        const lastAgentIdx = messages.map(m => m.participantId).lastIndexOf('agent')
        const recentMsgs = lastAgentIdx >= 0 ? messages.slice(lastAgentIdx + 1) : messages
        const hasRecentArtifacts = recentMsgs.some(m => m.type === 'artifact')
        const thinkingText = hasRecentArtifacts
          ? `${AGENT_DISPLAY_NAMES[currentAgent]} is reading your documents`
          : `${AGENT_DISPLAY_NAMES[currentAgent]} is writing`
        return (
          <div
            className="px-4 py-1.5 mono-label"
            style={{ color: AGENT_COLORS[currentAgent] }}
            aria-live="polite"
          >
            {thinkingText}
            <span className="caret" aria-hidden="true" />
          </div>
        )
      })()}

      {/* Solo completion: deliverables, summary email, and what to do next */}
      {session.status === 'completed' && session.isSolo && (
        <SoloCompletionScreen
          sessionId={sessionId}
          participantId={participantId}
          displayName={displayName}
          feedbackSubmitted={feedbackSubmitted}
          onDownload={handleDownloadSummary}
        />
      )}

      {/* Feedback Form: only after Scout signals session complete (team sessions) */}
      {session.status === 'completed' && !session.isSolo && !feedbackSubmitted && (
        <FeedbackForm
          sessionId={sessionId}
          participantId={participantId}
          displayName={displayName}
          hasSubmitted={feedbackSubmitted}
        />
      )}

      {/* Session Complete: download bundle after feedback (team sessions) */}
      {session.status === 'completed' && !session.isSolo && feedbackSubmitted && (
        <div className="mx-3 mb-2 bg-navy text-stone border-2 border-navy p-6 space-y-4">
          <p className="mono-label text-golden">Session complete</p>
          <h2 className="hl text-2xl text-stone">Your bundle is ready</h2>
          <p className="text-sm text-stone/75 max-w-md">
            System prompt, documentation, reflection responses, and the full conversation log.
          </p>
          <button onClick={handleDownloadSummary} className="btn btn-amber">
            <Download className="w-4 h-4" />
            Download the bundle
          </button>
        </div>
      )}

      {/* Message input, hidden after session complete */}
      {session.status !== 'completed' && (
        <MessageInput
          sessionId={sessionId}
          participantId={participantId}
          displayName={displayName}
        />
      )}

      {/* Reflection Panel Overlay */}
      {activeReflection && !reflectionMinimized && (
        <ReflectionPanel
          reflection={activeReflection}
          participants={participants}
          currentParticipantId={participantId}
          sessionId={sessionId}
          onSubmit={handleReflectionSubmit}
          isCreator={isCreator}
          onForceComplete={() => forceCompleteReflection(sessionId, activeReflection.id)}
          onMinimize={() => setReflectionMinimized(true)}
        />
      )}

      {/* Minimized reflection restore button */}
      {activeReflection && reflectionMinimized && (
        <button
          onClick={() => setReflectionMinimized(false)}
          className="fixed bottom-20 right-4 z-40 bg-navy text-stone mono-label px-4 py-3 border-2 border-golden shadow-[3px_3px_0_var(--color-navy-dark)] hover:bg-navy-lifted transition-colors cursor-pointer animate-slide-in-right flex items-center gap-2"
        >
          <span className="waypoint" aria-hidden="true" />
          Reflection {Object.keys(activeReflection.responses).length}/{participants.length}
        </button>
      )}

      {/* Backdrop for menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </div>
  )
}
